import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import { documentDownloadService } from '../../services/documentDownloadService';
import type { SignaturePlacementInputDto } from '../../services/documentSignatureService';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL ?? ''}/pdf.worker.min.mjs`;

/** Espelha PdfPlacementHelper.DefaultWidth (Infrastructure) — pontos PDF. */
const DEFAULT_SEAL_WIDTH = 185;
/** Espelha PdfPlacementHelper.DefaultHeight (Infrastructure) — pontos PDF, origem inferior-esquerda. */
const DEFAULT_SEAL_HEIGHT = 88;
const MAX_VIEWPORT_WIDTH = 720;
const RENDER_SCALE = 1.25;

export interface PdfSignaturePlacementEditorProps {
  /** Documento já persistido (download via API). */
  documentId?: string;
  /** Pré-visualização local antes do upload (ex.: fluxo de workflow no upload). */
  previewBlobUrl?: string | null;
  signerName: string;
  placement?: SignaturePlacementInputDto | null;
  onPlacementChange: (placement: SignaturePlacementInputDto | null) => void;
}

interface PageMetrics {
  widthPt: number;
  heightPt: number;
  renderWidthPx: number;
  renderHeightPx: number;
}

function clampPlacementToPage(
  x: number,
  y: number,
  width: number,
  height: number,
  pageWidthPt: number,
  pageHeightPt: number
): { x: number; y: number } {
  const maxX = Math.max(0, pageWidthPt - width);
  const maxY = Math.max(0, pageHeightPt - height);
  return {
    x: Math.max(0, Math.min(x, maxX)),
    y: Math.max(0, Math.min(y, maxY)),
  };
}

function placementToOverlayStyle(
  placement: SignaturePlacementInputDto,
  metrics: PageMetrics
): React.CSSProperties {
  const width = placement.width ?? DEFAULT_SEAL_WIDTH;
  const height = placement.height ?? DEFAULT_SEAL_HEIGHT;
  const leftPx = (placement.x / metrics.widthPt) * metrics.renderWidthPx;
  const bottomPx = (placement.y / metrics.heightPt) * metrics.renderHeightPx;
  const widthPx = (width / metrics.widthPt) * metrics.renderWidthPx;
  const heightPx = (height / metrics.heightPt) * metrics.renderHeightPx;

  return {
    left: `${leftPx}px`,
    bottom: `${bottomPx}px`,
    width: `${widthPx}px`,
    height: `${heightPx}px`,
  };
}

export const PdfSignaturePlacementEditor: React.FC<PdfSignaturePlacementEditorProps> = ({
  documentId,
  previewBlobUrl,
  signerName,
  placement,
  onPlacementChange,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const pdfDocRef = useRef<PDFDocumentProxy | null>(null);
  const renderTaskRef = useRef<ReturnType<PDFPageProxy['render']> | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pageCount, setPageCount] = useState(1);
  const [activePage, setActivePage] = useState(placement?.page ?? 1);
  const [pageMetrics, setPageMetrics] = useState<PageMetrics | null>(null);

  useEffect(() => {
    if (placement?.page)
    {
      setActivePage(placement.page);
    }
  }, [placement?.page]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    const loadPdf = async () => {
      setLoading(true);
      setError('');
      pdfDocRef.current?.destroy();
      pdfDocRef.current = null;

      try {
        let data: ArrayBuffer;
        if (previewBlobUrl)
        {
          const response = await fetch(previewBlobUrl);
          data = await response.arrayBuffer();
        }
        else if (documentId)
        {
          const blob = await documentDownloadService.downloadDocument(documentId);
          data = await blob.arrayBuffer();
        }
        else
        {
          setError('Documento não disponível para posicionamento.');
          return;
        }

        const pdf = await pdfjsLib.getDocument({ data }).promise;
        if (cancelled)
        {
          await pdf.destroy();
          return;
        }

        pdfDocRef.current = pdf;
        setPageCount(pdf.numPages);
        setActivePage((current) => Math.min(Math.max(1, current), pdf.numPages));
      }
      catch (err)
      {
        if (!cancelled)
        {
          setError(err instanceof Error ? err.message : 'Não foi possível carregar o PDF.');
        }
      }
      finally
      {
        if (!cancelled)
        {
          setLoading(false);
        }
      }
    };

    void loadPdf();

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
      pdfDocRef.current?.destroy();
      pdfDocRef.current = null;
      if (objectUrl)
      {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [documentId, previewBlobUrl]);

  useEffect(() => {
    const pdf = pdfDocRef.current;
    const canvas = canvasRef.current;
    if (!pdf || !canvas || loading)
    {
      return;
    }

    let cancelled = false;

    const renderPage = async () => {
      renderTaskRef.current?.cancel();

      try {
        const page = await pdf.getPage(activePage);
        if (cancelled)
        {
          return;
        }

        const baseViewport = page.getViewport({ scale: 1 });
        const pageWidthPt = baseViewport.width;
        const pageHeightPt = baseViewport.height;
        const fitScale = Math.min(
          MAX_VIEWPORT_WIDTH / pageWidthPt,
          (MAX_VIEWPORT_WIDTH * 1.35) / pageHeightPt
        );
        const scale = fitScale * RENDER_SCALE;
        const viewport = page.getViewport({ scale });
        const context = canvas.getContext('2d');
        if (!context)
        {
          return;
        }

        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);

        const task = page.render({
          canvasContext: context,
          viewport,
        });
        renderTaskRef.current = task;
        await task.promise;

        if (cancelled)
        {
          return;
        }

        setPageMetrics({
          widthPt: pageWidthPt,
          heightPt: pageHeightPt,
          renderWidthPx: viewport.width,
          renderHeightPx: viewport.height,
        });
      }
      catch (err)
      {
        if (!cancelled && err instanceof Error && err.name !== 'RenderingCancelledException')
        {
          setError(err.message || 'Não foi possível renderizar a página.');
        }
      }
    };

    void renderPage();

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
    };
  }, [activePage, loading, pageCount]);

  const handleOverlayClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!pageMetrics || !overlayRef.current)
      {
        return;
      }

      const rect = overlayRef.current.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const clickY = event.clientY - rect.top;
      const width = placement?.width ?? DEFAULT_SEAL_WIDTH;
      const height = placement?.height ?? DEFAULT_SEAL_HEIGHT;

      const xPt =
        (clickX / pageMetrics.renderWidthPx) * pageMetrics.widthPt - width / 2;
      const yPt =
        (1 - clickY / pageMetrics.renderHeightPx) * pageMetrics.heightPt - height / 2;

      const clamped = clampPlacementToPage(
        xPt,
        yPt,
        width,
        height,
        pageMetrics.widthPt,
        pageMetrics.heightPt
      );

      onPlacementChange({
        page: activePage,
        x: clamped.x,
        y: clamped.y,
        width,
        height,
      });
    },
    [activePage, onPlacementChange, pageMetrics, placement?.height, placement?.width]
  );

  const handlePageChange = (page: number) => {
    const nextPage = Math.min(Math.max(1, page), pageCount);
    setActivePage(nextPage);
    if (placement)
    {
      onPlacementChange({ ...placement, page: nextPage });
    }
  };

  if (loading)
  {
    return (
      <div
        className="flex min-h-[280px] flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50 px-6 py-10"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div className="mb-4 h-12 w-12 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
        <p className="text-center text-base font-medium text-gray-900">
          Carregando PDF para posicionamento
        </p>
        <p className="mt-2 max-w-sm text-center text-sm text-gray-500">
          Aguarde alguns segundos. Em seguida você poderá clicar na página para posicionar a
          assinatura.
        </p>
      </div>
    );
  }

  if (error)
  {
    return <p className="text-sm text-red-600">{error}</p>;
  }

  return (
    <div className="space-y-2">
      <p className="text-sm text-gray-600">
        Clique na página para posicionar a assinatura de <strong>{signerName}</strong>. O retângulo
        indica o tamanho do selo no PDF final. Deixe em branco para usar o rodapé da última página.
      </p>
      <div className="max-h-[min(70vh,520px)] overflow-auto rounded-lg border border-gray-200 bg-gray-100 p-2">
        <div
          className="relative mx-auto w-fit"
          style={
            pageMetrics
              ? { width: pageMetrics.renderWidthPx, height: pageMetrics.renderHeightPx }
              : undefined
          }
        >
          <canvas ref={canvasRef} className="block shadow-sm" />
          {pageMetrics && (
            <div
              ref={overlayRef}
              className="absolute inset-0 cursor-crosshair"
              onClick={handleOverlayClick}
              role="presentation"
            >
              {placement && placement.page === activePage && (
                <div
                  className="pointer-events-none absolute rounded border-2 border-indigo-500 bg-indigo-100/40"
                  style={placementToOverlayStyle(placement, pageMetrics)}
                />
              )}
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs text-gray-600">
          Página
          <input
            type="number"
            min={1}
            max={pageCount}
            className="ml-1 w-14 rounded border border-gray-300 px-1 py-0.5 text-xs"
            value={activePage}
            onChange={(e) => handlePageChange(Number(e.target.value) || 1)}
          />
          <span className="ml-1 text-gray-400">/ {pageCount}</span>
        </label>
        <button
          type="button"
          className="text-xs text-indigo-600 hover:underline"
          onClick={() => onPlacementChange(null)}
        >
          Usar posição padrão (final do documento)
        </button>
      </div>
    </div>
  );
};
